import { ProposalLineItemPriceInput } from '@/gql/graphql';
import { TFormState } from '@/ui/components/addEditProposalLineItem/formState';

type SavedMaterial = {
  perUnitMaterialQuantity: number;
  material: { id: string };
};

export type MaterialQuantityChange = {
  materialId: string;
  quantityChange: { absolute: number };
};

/**
 * Diffs the form's linked materials against the last-saved set, producing the
 * quantity-change list a materials mutation expects (removed materials become
 * an absolute quantity of 0) and whether the set is unchanged. Shared by the
 * Proposal and Estimate line item editors.
 */
export const diffLinkedMaterials = (
  savedMaterials: ReadonlyArray<SavedMaterial>,
  formMaterials: TFormState['materials']
): { changes: MaterialQuantityChange[]; unchanged: boolean } => {
  const removed = savedMaterials.filter(
    (saved) =>
      !formMaterials.some((current) => current.id === saved.material.id)
  );

  const unchanged =
    removed.length === 0 &&
    savedMaterials.length === formMaterials.length &&
    formMaterials.every((current) => {
      const saved = savedMaterials.find((s) => s.material.id === current.id);
      return saved?.perUnitMaterialQuantity === current.quantity;
    });

  const changes: MaterialQuantityChange[] = [
    ...removed.map((saved) => ({
      materialId: saved.material.id,
      quantityChange: { absolute: 0 },
    })),
    ...formMaterials.map((current) => ({
      materialId: current.id,
      quantityChange: { absolute: current.quantity },
    })),
  ];

  return { changes, unchanged };
};

/**
 * Derives the line item price input (a manual unit price, or a material-cost
 * markup as a percentage or amount) from the editor's markup form state. Shared
 * by the Proposal and Estimate line item editors so the markup maths has a
 * single source of truth.
 */
export const derivePriceInput = (
  state: TFormState
): Pick<
  ProposalLineItemPriceInput,
  'unitPriceExcTax' | 'markupPercentage' | 'markupAmount'
> => {
  switch (true) {
    case state.markupTotals.markupType === 'profitPercentage' &&
      state.autoUpdatePrice: {
      const profitFactor = (state.markupTotals.profitPercentage || 0) / 100;
      const markupPercentage =
        profitFactor !== 0 ? (profitFactor / (1 - profitFactor)) * 100 : 0;
      return { markupPercentage };
    }
    case state.markupTotals.markupType === 'profitAmount' &&
      state.autoUpdatePrice:
      return { markupAmount: state.markupTotals.profitAmount };
    default:
      return { unitPriceExcTax: state.markupTotals.price };
  }
};
